/**
 * Factory preset bank — read-only curated patches shipped with the synth.
 *
 * Why TypeScript literals (not JSON file)?
 *   - Type-checked at compile time against the Preset schema; a renamed
 *     param or missing module is caught before shipping.
 *   - Bundled into the SPA, so they work offline out of the box without
 *     service-worker pre-cache gymnastics.
 *   - Easier to keep in sync with module param names during refactors —
 *     VS Code jumps straight to the definition.
 *
 * Each preset is a full snapshot: all nine modules are included with
 * explicit values so loading a factory preset always resets the synth to
 * a known state, even if the user has randomized or twisted things
 * beforehand. Factory presets aren't persisted to Supabase — they live
 * in memory and render alongside the user's presets in the browser.
 */

import type { ModuleState } from '@ac-slayer/synth-core';
import type { ModCurve } from '@ac-slayer/synth-core';
import type { Preset, PresetEffectInstance } from '@ac-slayer/synth-presets';
import { CURRENT_SCHEMA_VERSION } from '@ac-slayer/synth-presets';

// ── Per-module param typings ──────────────────────────────────────────
// These stay in step with each module's parameter declarations. If the
// real module's param name changes, TS won't catch the drift (params is
// Record<string, number|string>), but at least our helper accepts named
// keys so typos show up here instead of at runtime.

interface OscParams {
  // 'pulse' and 'capture' are valid OscillatorModule modes too. The
  // capture mode needs a wavetable to play and isn't worth shipping in
  // factory presets, but pulse + Width is great for PWM patches.
  Wave?: 'sine' | 'square' | 'saw' | 'triangle' | 'pulse';
  Coarse?: number;
  Detune?: number;
  Level?: number;
  Width?: number;
  Drive?: number;
  Fold?: number;
}

interface FilterParams {
  Type?: 'lowpass' | 'highpass' | 'bandpass' | 'notch';
  Cutoff?: number;
  Q?: number;
}

interface EnvParams {
  Attack?: number;
  Decay?: number;
  Sustain?: number;
  Release?: number;
}

interface VcaParams {
  Gain?: number;
}

interface LfoParams {
  Wave?: 'sine' | 'triangle' | 'sawtooth' | 'square';
  Sync?: string;
  Rate?: number;
}

interface ShParams {
  Rate?: number;
  Slew?: number;
}

interface NoiseParams {
  Color?: 'white' | 'pink' | 'brown';
  Level?: number;
}

interface DistParams {
  Drive?: number;
  Tone?: number;
  Mix?: number;
}

interface DelayParams {
  Sync?: string;
  Time?: number;
  Feedback?: number;
  Mix?: number;
}

interface CrushParams {
  Bits?: number;
  Freq?: number;
  Mix?: number;
}

interface ReverbParams {
  Size?: number;
  Damp?: number;
  Mix?: number;
}

interface ChorusParams {
  Rate?: number;
  Depth?: number;
  Mix?: number;
}

interface FlangerParams {
  Rate?: number;
  Depth?: number;
  Feedback?: number;
  Mix?: number;
}

interface PhaserParams {
  Rate?: number;
  Depth?: number;
  Feedback?: number;
  Mix?: number;
}

interface RingmodParams {
  Freq?: number;
  Mix?: number;
}

interface CompressorParams {
  Threshold?: number;
  Ratio?: number;
  Attack?: number;
  Release?: number;
  Makeup?: number;
}

/** A sparse spec for a factory preset. Anything omitted falls back to a
 *  neutral default that keeps the module out of the way of the sound. */
interface PresetSpec {
  readonly slug: string;
  readonly name: string;
  readonly tags: readonly string[];
  readonly bpm?: number;
  readonly voice?: {
    readonly mode?: 'poly' | 'mono' | 'legato';
    readonly glideMs?: number;
    readonly unison?: number;
    readonly unisonSpread?: number;
  };
  readonly osc?: OscParams;
  readonly filter?: FilterParams;
  readonly env?: EnvParams;
  readonly vca?: VcaParams;
  readonly lfo?: LfoParams;
  readonly sh?: ShParams;
  readonly noise?: NoiseParams;
  readonly dist?: DistParams;
  readonly delay?: DelayParams;
  readonly crush?: CrushParams;
  readonly reverb?: ReverbParams;
  readonly chorus?: ChorusParams;
  readonly flanger?: FlangerParams;
  readonly phaser?: PhaserParams;
  readonly ringmod?: RingmodParams;
  readonly compressor?: CompressorParams;
  readonly mods?: ReadonlyArray<{
    readonly source: string;
    readonly target: string;
    readonly param: string;
    readonly depth: number;
    /** Optional bipolar range multiplier — typical values 0.25 / 0.5 / 1 / 2.
     *  Lets a factory preset declare e.g. "macro 1 sweeps cutoff at 2× the
     *  default range" without baking that into the depth alone. */
    readonly rangeMul?: number;
    /** Optional macro response curve. Only applies to macro sources. */
    readonly curve?: ModCurve;
  }>;
  readonly macros?: readonly [number, number, number, number];
}

/** Jan 1, 2026 UTC — stable "born on" date for every factory preset so
 *  the timestamp column in the browser doesn't shift with every deploy. */
const FACTORY_EPOCH = Date.UTC(2026, 0, 1);

function toModuleStates(spec: PresetSpec): ModuleState[] {
  // Neutral defaults keep modules musical when the preset doesn't touch
  // them. Gains kept conservative so switching presets isn't a volume
  // jump-scare. Effects live in `toEffectInstances` (v4 schema).
  return [
    {
      id: 'osc',
      type: 'oscillator',
      params: { Wave: 'saw', Coarse: 0, Detune: 0, Level: 0.7, ...spec.osc },
    },
    {
      id: 'filter',
      type: 'filter',
      params: { Type: 'lowpass', Cutoff: 5000, Q: 1, ...spec.filter },
    },
    {
      id: 'env',
      type: 'envelope',
      params: {
        Attack: 0.01,
        Decay: 0.2,
        Sustain: 0.7,
        Release: 0.3,
        ...spec.env,
      },
    },
    {
      id: 'vca',
      type: 'vca',
      params: { Gain: 0.7, ...spec.vca },
    },
    {
      id: 'lfo',
      type: 'lfo',
      params: { Wave: 'sine', Sync: 'free', Rate: 2, ...spec.lfo },
    },
    {
      id: 'sh',
      type: 'sh',
      params: { Rate: 2, Slew: 0, ...spec.sh },
    },
    {
      id: 'noise',
      type: 'noise',
      // Match the runtime defaults (level 0 = silent) so existing
      // factory presets that don't touch noise stay unchanged. Patches
      // that want noise (the airy fart, e.g.) override Level here.
      params: { Color: 'white', Level: 0, ...spec.noise },
    },
  ];
}

/**
 * v4 effect instances for a factory preset. Default chain composition
 * matches the runtime DEFAULT_EFFECT_CHAIN so loading a factory patch
 * lands on a familiar layout. Mix=0 on every effect means "audibly
 * inert" until the spec dials something in. Ids match the runtime
 * defaults so modConnections referencing 'dist'/'delay'/etc. keep
 * resolving.
 */
function toEffectInstances(spec: PresetSpec): PresetEffectInstance[] {
  return [
    {
      type: 'compressor',
      id: 'compressor',
      params: { ...spec.compressor },
    },
    {
      type: 'distortion',
      id: 'dist',
      params: { Drive: 0, Tone: 4000, Mix: 0, ...spec.dist },
    },
    {
      type: 'ringmod',
      id: 'ringmod',
      params: { Mix: 0, ...spec.ringmod },
    },
    {
      type: 'chorus',
      id: 'chorus',
      params: { Mix: 0, ...spec.chorus },
    },
    {
      type: 'flanger',
      id: 'flanger',
      params: { Mix: 0, ...spec.flanger },
    },
    {
      type: 'phaser',
      id: 'phaser',
      params: { Mix: 0, ...spec.phaser },
    },
    {
      type: 'delay',
      id: 'delay',
      params: { Sync: 'free', Time: 0.3, Feedback: 0.3, Mix: 0, ...spec.delay },
    },
    {
      type: 'bitcrusher',
      id: 'bitcrusher',
      params: { Bits: 16, Freq: 48000, Mix: 0, ...spec.crush },
    },
    {
      type: 'reverb',
      id: 'reverb',
      params: { Mix: 0, ...spec.reverb },
    },
  ];
}

function makePreset(spec: PresetSpec): Preset {
  // Use the first tag as the category so grouping matches the tagline,
  // e.g. "ABYSS SUB" → tags: ['bass', 'mono'] → category: 'Bass'. Title-
  // case for display polish.
  const firstTag = spec.tags[0] ?? 'Factory';
  const category = firstTag.charAt(0).toUpperCase() + firstTag.slice(1).toLowerCase();
  return {
    schemaVersion: CURRENT_SCHEMA_VERSION,
    id: `preset-factory-${spec.slug}`,
    name: spec.name,
    author: 'factory',
    category,
    createdAt: FACTORY_EPOCH,
    updatedAt: FACTORY_EPOCH,
    tags: spec.tags,
    bpm: spec.bpm ?? 120,
    voice: {
      polyphony: 8,
      mode: spec.voice?.mode ?? 'poly',
      glideMs: spec.voice?.glideMs ?? 0,
      unison: spec.voice?.unison ?? 1,
      unisonSpread: spec.voice?.unisonSpread ?? 15,
    },
    modules: toModuleStates(spec),
    effects: toEffectInstances(spec),
    modConnections: (spec.mods ?? []).map((m, i) => ({
      id: `fac-${spec.slug}-${i}`,
      sourceModuleId: m.source,
      targetModuleId: m.target,
      targetParam: m.param,
      depth: m.depth,
      // Optional fields are only spread when set, so JSON snapshots of
      // simple presets stay tidy and the router's defaults take over.
      ...(m.rangeMul !== undefined ? { rangeMul: m.rangeMul } : {}),
      ...(m.curve !== undefined ? { curve: m.curve } : {}),
    })),
    macros: spec.macros ?? [0, 0, 0, 0],
  };
}

// ── The bank ──────────────────────────────────────────────────────────
// Curated to span bass, lead, pad, drone, pluck, and noisier territory
// so the first-visit user can hear what the synth can do in a dozen
// clicks. Names lean into the "evil synth" aesthetic on purpose.

const SPECS: PresetSpec[] = [
  {
    slug: 'abyss-sub',
    name: 'ABYSS SUB',
    tags: ['bass', 'mono'],
    voice: { mode: 'mono', glideMs: 12 },
    osc: { Wave: 'sine', Coarse: -12, Level: 0.9 },
    filter: { Cutoff: 260, Q: 0.6 },
    env: { Attack: 0.002, Decay: 0.08, Sustain: 0.85, Release: 0.18 },
    vca: { Gain: 0.65 },
  },
  {
    slug: 'acid-worm',
    name: 'ACID WORM',
    tags: ['bass', 'lead', 'mono'],
    voice: { mode: 'mono', glideMs: 40 },
    osc: { Wave: 'saw', Coarse: -12, Level: 0.9 },
    filter: { Cutoff: 1800, Q: 18 },
    env: { Attack: 0.001, Decay: 0.18, Sustain: 0.25, Release: 0.12 },
    vca: { Gain: 0.55 },
    dist: { Drive: 0.35, Tone: 3200, Mix: 0.5 },
    delay: { Sync: '1/8', Feedback: 0.38, Mix: 0.2 },
    mods: [
      // Velocity opens the filter — classic 303 behavior.
      { source: 'velocity', target: 'filter', param: 'Cutoff', depth: 0.45 },
    ],
  },
  {
    slug: 'bone-pluck',
    name: 'BONE PLUCK',
    tags: ['pluck', 'poly'],
    osc: { Wave: 'saw', Level: 0.7 },
    filter: { Cutoff: 4200, Q: 3 },
    env: { Attack: 0.003, Decay: 0.22, Sustain: 0, Release: 0.08 },
    vca: { Gain: 0.55 },
    delay: { Sync: '1/8', Feedback: 0.55, Mix: 0.35 },
    mods: [
      { source: 'velocity', target: 'vca', param: 'Gain', depth: 0.3 },
    ],
  },
  {
    slug: 'spectre-pad',
    name: 'SPECTRE PAD',
    tags: ['pad', 'poly', 'unison'],
    voice: { unison: 3, unisonSpread: 20 },
    osc: { Wave: 'triangle', Level: 0.7 },
    filter: { Cutoff: 3200, Q: 2 },
    env: { Attack: 0.9, Decay: 0.5, Sustain: 0.9, Release: 1.4 },
    vca: { Gain: 0.55 },
    lfo: { Wave: 'sine', Rate: 0.8 },
    delay: { Sync: '1/2', Feedback: 0.45, Mix: 0.28 },
    mods: [
      // Slow LFO breathes the cutoff — that "pad lift" feel.
      { source: 'lfo', target: 'filter', param: 'Cutoff', depth: 0.2 },
    ],
  },
  {
    slug: 'arctic-drone',
    name: 'ARCTIC DRONE',
    tags: ['drone', 'unison'],
    voice: { unison: 5, unisonSpread: 28 },
    osc: { Wave: 'saw', Detune: -6, Level: 0.8 },
    filter: { Cutoff: 1400, Q: 3 },
    env: { Attack: 1.6, Decay: 0.3, Sustain: 1, Release: 3 },
    vca: { Gain: 0.55 },
    lfo: { Wave: 'triangle', Rate: 0.25 },
    sh: { Rate: 0.6, Slew: 0.2 },
    mods: [
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.15 },
      { source: 'sh', target: 'filter', param: 'Cutoff', depth: 0.35 },
    ],
  },
  {
    slug: 'siren-stab',
    name: 'SIREN STAB',
    tags: ['lead', 'mono', 'fx'],
    voice: { mode: 'mono' },
    osc: { Wave: 'square', Level: 0.85 },
    filter: { Type: 'bandpass', Cutoff: 2200, Q: 6 },
    env: { Attack: 0.001, Decay: 0.06, Sustain: 0.75, Release: 0.22 },
    vca: { Gain: 0.5 },
    lfo: { Wave: 'sawtooth', Rate: 3.5 },
    dist: { Drive: 0.55, Tone: 3500, Mix: 0.55 },
    crush: { Bits: 7, Freq: 6000, Mix: 0.6 },
    mods: [
      { source: 'lfo', target: 'filter', param: 'Cutoff', depth: 0.5 },
    ],
  },
  {
    slug: 'bitstep',
    name: 'BITSTEP',
    tags: ['seq', 'mono', 'fx'],
    voice: { mode: 'mono', glideMs: 6 },
    osc: { Wave: 'square', Level: 0.9 },
    filter: { Cutoff: 1600, Q: 5 },
    env: { Attack: 0.001, Decay: 0.18, Sustain: 0.2, Release: 0.1 },
    vca: { Gain: 0.55 },
    sh: { Rate: 8, Slew: 0 },
    crush: { Bits: 5, Freq: 8000, Mix: 0.75 },
    delay: { Sync: '1/16', Feedback: 0.42, Mix: 0.3 },
    mods: [
      // S&H staircase on cutoff is the sound of this patch.
      { source: 'sh', target: 'filter', param: 'Cutoff', depth: 0.7 },
    ],
  },
  {
    slug: 'voltage-lead',
    name: 'VOLTAGE LEAD',
    tags: ['lead', 'mono'],
    voice: { mode: 'mono', glideMs: 22 },
    osc: { Wave: 'saw', Level: 0.9 },
    filter: { Cutoff: 4500, Q: 7 },
    env: { Attack: 0.001, Decay: 0.3, Sustain: 0.6, Release: 0.2 },
    vca: { Gain: 0.5 },
    lfo: { Wave: 'triangle', Rate: 5.5 },
    dist: { Drive: 0.6, Tone: 4500, Mix: 0.85 },
    mods: [
      { source: 'lfo', target: 'filter', param: 'Cutoff', depth: 0.25 },
      { source: 'velocity', target: 'filter', param: 'Cutoff', depth: 0.35 },
    ],
  },
  // ── New patches that exercise mod-wheel routing, macro curves, and
  // the modulation / time effects (chorus, phaser, reverb, compressor).
  {
    slug: 'glass-bells',
    name: 'GLASS BELLS',
    tags: ['pluck', 'poly', 'fx'],
    osc: { Wave: 'triangle', Level: 0.7 },
    filter: { Cutoff: 6500, Q: 1.5 },
    env: { Attack: 0.001, Decay: 0.6, Sustain: 0, Release: 0.4 },
    vca: { Gain: 0.55 },
    chorus: { Rate: 0.6, Depth: 0.5, Mix: 0.35 },
    reverb: { Size: 0.65, Damp: 0.3, Mix: 0.45 },
    // Macro 1 = "shimmer": exp curve so the bottom half of the macro
    // barely opens the filter, the top half blooms it. Same macro also
    // adds a touch of reverb tail (linear) — feels like one knob, two
    // characters.
    macros: [0.25, 0, 0, 0],
    mods: [
      {
        source: 'macro-1',
        target: 'filter',
        param: 'Cutoff',
        depth: 0.55,
        curve: 'exp',
      },
      {
        source: 'macro-1',
        target: 'reverb',
        param: 'Mix',
        depth: 0.25,
      },
      { source: 'velocity', target: 'vca', param: 'Gain', depth: 0.4 },
    ],
  },
  {
    slug: 'tide-pad',
    name: 'TIDE PAD',
    tags: ['pad', 'poly', 'unison'],
    voice: { unison: 3, unisonSpread: 14 },
    osc: { Wave: 'saw', Level: 0.65 },
    filter: { Cutoff: 1200, Q: 2 },
    env: { Attack: 1.2, Decay: 0.4, Sustain: 0.85, Release: 1.8 },
    vca: { Gain: 0.5 },
    lfo: { Wave: 'sine', Rate: 0.4 },
    chorus: { Rate: 0.4, Depth: 0.7, Mix: 0.5 },
    reverb: { Size: 0.7, Damp: 0.4, Mix: 0.4 },
    // Mod wheel opens the cutoff dramatically — at 0 the pad is muffled
    // and distant; at 127 it blooms full-spectrum. S-curve so the
    // middle of the wheel feels expressive instead of either-or.
    mods: [
      {
        source: 'modwheel',
        target: 'filter',
        param: 'Cutoff',
        depth: 0.7,
      },
      { source: 'lfo', target: 'osc', param: 'Detune', depth: 0.15 },
    ],
  },
  {
    slug: 'midnight-keys',
    name: 'MIDNIGHT KEYS',
    tags: ['keys', 'poly'],
    osc: { Wave: 'triangle', Level: 0.75 },
    filter: { Cutoff: 3200, Q: 1.5 },
    env: { Attack: 0.003, Decay: 0.4, Sustain: 0.45, Release: 0.35 },
    vca: { Gain: 0.55 },
    compressor: { Threshold: -22, Ratio: 4, Attack: 0.005, Release: 0.12, Makeup: 4 },
    phaser: { Rate: 0.35, Depth: 0.6, Feedback: 0.45, Mix: 0.4 },
    delay: { Sync: '1/4', Feedback: 0.35, Mix: 0.18 },
    reverb: { Size: 0.5, Damp: 0.5, Mix: 0.25 },
    mods: [
      // Velocity bumps brightness — touch sensitivity on an EP-ish patch.
      { source: 'velocity', target: 'filter', param: 'Cutoff', depth: 0.4 },
    ],
  },
  {
    slug: 'metal-drone',
    name: 'METAL DRONE',
    tags: ['drone', 'fx'],
    voice: { mode: 'legato', glideMs: 80 },
    osc: { Wave: 'saw', Coarse: -12, Level: 0.85 },
    filter: { Cutoff: 900, Q: 6 },
    env: { Attack: 1.2, Decay: 0.4, Sustain: 1, Release: 2.2 },
    vca: { Gain: 0.55 },
    lfo: { Wave: 'triangle', Rate: 0.18 },
    sh: { Rate: 0.4, Slew: 0.4 },
    ringmod: { Freq: 90, Mix: 0.3 },
    dist: { Drive: 0.4, Tone: 2200, Mix: 0.45 },
    reverb: { Size: 0.85, Damp: 0.2, Mix: 0.55 },
    mods: [
      // S&H drifts the ring-mod frequency for that "broken radio" feel.
      { source: 'sh', target: 'ringmod', param: 'Freq', depth: 0.5 },
      { source: 'lfo', target: 'filter', param: 'Cutoff', depth: 0.3 },
    ],
  },
  {
    slug: 'wheel-howl',
    name: 'WHEEL HOWL',
    tags: ['lead', 'mono'],
    voice: { mode: 'mono', glideMs: 18 },
    osc: { Wave: 'saw', Level: 0.85 },
    filter: { Cutoff: 1600, Q: 8 },
    env: { Attack: 0.002, Decay: 0.25, Sustain: 0.7, Release: 0.25 },
    vca: { Gain: 0.5 },
    lfo: { Wave: 'sine', Rate: 5.5 },
    flanger: { Rate: 0.45, Depth: 0.45, Feedback: 0.5, Mix: 0.4 },
    delay: { Sync: '1/4', Feedback: 0.4, Mix: 0.22 },
    // Mod wheel routes to LFO depth on cutoff — wheel up = vibrato that
    // turns into wah-howl. Demonstrates wheel-as-source plus a "depth
    // controlled by another mod" pattern. Also macro 1 = filter Q on
    // a log curve (fast snap into resonance).
    macros: [0.0, 0, 0, 0],
    mods: [
      { source: 'lfo', target: 'filter', param: 'Cutoff', depth: 0.25 },
      // Mod wheel scales filter cutoff aggressively with rangeMul=1.5
      // so the wheel can sweep further than the default range.
      {
        source: 'modwheel',
        target: 'filter',
        param: 'Cutoff',
        depth: 0.6,
        rangeMul: 1.5,
      },
      {
        source: 'macro-1',
        target: 'filter',
        param: 'Q',
        depth: 0.7,
        curve: 'log',
      },
    ],
  },
];

/** The curated factory preset bank in the order they appear in the browser. */
export const FACTORY_PRESETS: readonly Preset[] = SPECS.map(makePreset);

// ── Secret bank ──────────────────────────────────────────────────────
// Hidden until the user holds Shift and types the unlock phrase
// (handled in main.ts). Each patch is dialed in to evoke a different
// flavor of the digestive low-frequency arts. Names lean into the
// joke; sound design tries to actually deliver — short, percussive
// bursts for some; sustained airy tones for others; pitched bends
// for the "questioning" toot.

// ── Sound design notes for the fart bank ────────────────────────────
// What actually makes the ear hear "fart": rapid amplitude buzzing
// at 15-25 Hz on top of a low-pitched carrier. That's the "lip
// flapping" or sphincter vibration character — fundamentally
// audio-rate amplitude modulation, not just a tone with a pitch
// sweep.
//
// The recipe per patch:
//
//   1. Carrier: pulse or saw at a LOW pitch (Coarse -19..-24 so
//      C4-press lands around 50-90 Hz). Pulse with narrow Width
//      gives the buzziest harmonic content; saw with fold adds grit.
//   2. **Square LFO at 16-25 Hz routed to OSC.Level with strong
//      negative depth**. This is the fart's signature: the gain
//      pulses on/off many times per second, producing the
//      "BLBLBLBL" texture. Without this, you just have a buzzy bass.
//   3. Lowpass filter at 200-500 Hz with moderate Q (3-6) — keeps
//      the sound dark and gives it some throat-like resonance.
//   4. Envelope shapes the overall burst: short attack, modest
//      decay, low/no sustain so each note is a discrete fart.
//   5. Optional envelope → OSC.Coarse with small positive depth
//      for a brief "pop up" pitch flick at the start.
//
// The LFO "chop" rate sets the fart's character — slower (12-15 Hz)
// reads as a wet sputter, faster (20-28 Hz) as a tight rapid blat.
// Mono everywhere; farts don't chord.
const FART_SPECS: PresetSpec[] = [
  // Long deep rumble. Pulse wave 2 octaves down (~65 Hz at C4),
  // narrow Width for a buzzy harmonic stack. Slow LFO chop at
  // 14 Hz makes the "BLBLBLBL" character read as a sustained
  // bowel rumble. Lowpass keeps it dark; light fold adds grit.
  {
    slug: 'fart-brown-note',
    name: 'THE BROWN NOTE',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'pulse', Coarse: -24, Width: 0.3, Level: 0.6, Drive: 0.2, Fold: 2 },
    filter: { Type: 'lowpass', Cutoff: 280, Q: 4 },
    env: { Attack: 0.005, Decay: 0.3, Sustain: 0.85, Release: 0.4 },
    vca: { Gain: 0.7 },
    lfo: { Wave: 'square', Rate: 14 },
    mods: [
      // Square LFO at 14 Hz × depth 0.9 swings OSC.Level by ±0.9
      // around its 0.6 baseline → 0..1.5 effective gain, clamped at
      // 1, giving a gated "blab blab blab" at ~14 chops/sec.
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.9 },
    ],
  },

  // Classic short toot. Pulse 19 semis down (~87 Hz), 22 Hz chop —
  // tight rapid blat, "phthpthpht". Zero sustain so it cuts off as
  // soon as the key releases. Tiny envelope pitch flick for the
  // initial "pop".
  {
    slug: 'fart-pull-my-finger',
    name: 'PULL MY FINGER',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'pulse', Coarse: -19, Width: 0.25, Level: 0.6 },
    filter: { Type: 'lowpass', Cutoff: 380, Q: 5 },
    env: { Attack: 0.002, Decay: 0.22, Sustain: 0, Release: 0.05 },
    vca: { Gain: 0.75 },
    dist: { Drive: 0.3, Tone: 2200, Mix: 0.35 },
    lfo: { Wave: 'square', Rate: 22 },
    mods: [
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.95 },
      // Envelope → Detune (audio-rate path); modScale 100 cents/depth.
      // 4 semitones of "pop up" at note start.
      { source: 'envelope', target: 'osc', param: 'Detune', depth: 4 },
    ],
  },

  // The wisp. More noise + slower, gentler chop (8 Hz triangle).
  // Lifts the noise rhythmically without the hard gating that makes
  // the others sound percussive. Bandpass on noise + small reverb
  // for that small-tiled-room feel.
  {
    slug: 'fart-whispering-gassy-winds',
    name: 'WHISPERING GASSY WINDS',
    tags: ['fart', 'pad'],
    osc: { Wave: 'pulse', Coarse: -14, Width: 0.4, Level: 0.15 },
    noise: { Color: 'pink', Level: 0.7 },
    filter: { Type: 'bandpass', Cutoff: 900, Q: 4 },
    env: { Attack: 0.15, Decay: 0.35, Sustain: 0.6, Release: 0.4 },
    vca: { Gain: 0.55 },
    reverb: { Size: 0.45, Damp: 0.5, Mix: 0.25 },
    lfo: { Wave: 'triangle', Rate: 8 },
    mods: [
      // Triangle (not square) gives a softer ebb/flow chop —
      // less "BLBL", more "shhwhhwhh".
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.6 },
    ],
  },

  // Interrogative — a single "uhhhh?" that pitches UP and then stops
  // on its own. Self-terminating envelope (Sustain: 0) so holding the
  // key down doesn't extend the note past the rise — the envelope
  // ramps 0→peak across the attack (pitch climbs), then snaps to
  // silence in the short decay. No LFO chop on this one: the
  // continuous gating was making it read as a sustained farting
  // sound rather than a one-shot question. The carrier's pulse +
  // resonant lowpass still gives it the buzzy fart character; the
  // pitch glide does the rest.
  //
  // Routing the envelope to OSC.Detune (NOT Coarse) — `Coarse` has no
  // per-voice AudioParam in the audio graph, so per-note audio-rate
  // sources like the envelope can't actually drive it. `Detune` maps
  // to the live `osc.detune` AudioParam (in cents) so the envelope
  // signal flows correctly. modScale for Detune is 100 cents = 1
  // semitone at depth=1, so depth=12 gives a full octave climb.
  {
    slug: 'fart-was-that-a-question',
    name: 'WAS THAT A QUESTION?',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'pulse', Coarse: -19, Width: 0.3, Level: 0.7 },
    filter: { Type: 'lowpass', Cutoff: 360, Q: 6 },
    // 400ms attack ramps the pitch up. 80ms decay-to-zero cuts the
    // sound cleanly at the peak — sustain: 0 means the envelope
    // doesn't hold even if the user keeps the key down. Release is
    // tiny so a key-up mid-attack doesn't leave a long tail.
    env: { Attack: 0.4, Decay: 0.08, Sustain: 0, Release: 0.03 },
    vca: { Gain: 0.7 },
    mods: [
      // depth 12 × modScale 100 = ~1200 cents (a full octave) rise
      // across the 400ms attack. Audio-rate path (envelope → osc.detune
      // AudioParam) is what makes the glissando actually audible.
      { source: 'envelope', target: 'osc', param: 'Detune', depth: 12 },
    ],
  },

  // Big chesty rumble. Pulse 2+ octaves down with heavy fold + dist
  // + slow chop (12 Hz) for the deepest, slowest fart. The fold
  // adds harmonic spray, dist adds compression-style grit.
  {
    slug: 'fart-thunderstruck-bottom',
    name: 'THUNDERSTRUCK BOTTOM',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'pulse', Coarse: -24, Width: 0.35, Level: 0.6, Drive: 0.5, Fold: 3 },
    filter: { Type: 'lowpass', Cutoff: 220, Q: 5 },
    env: { Attack: 0.005, Decay: 0.4, Sustain: 0.7, Release: 0.45 },
    vca: { Gain: 0.7 },
    dist: { Drive: 0.55, Tone: 1500, Mix: 0.55 },
    crush: { Bits: 9, Freq: 10000, Mix: 0.25 },
    lfo: { Wave: 'square', Rate: 12 },
    mods: [
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.95 },
      // Envelope → Detune for audio-rate pitch sweep — ~6 semis up
      // at attack peak, decays back as the envelope settles.
      { source: 'envelope', target: 'osc', param: 'Detune', depth: 6 },
    ],
  },

  // A nasal squeak that GLISSES UP across the duration of the note —
  // questioning intonation. Recipe: a long-attack envelope (~280ms
  // ramp) routed POSITIVELY to OSC.Coarse with depth ~1.7. While
  // the envelope opens, pitch climbs from the static Coarse up by
  // ~12 semitones, which the ear reads as a rising glide / "huh?"
  // inflection. After the attack peaks the envelope holds at the
  // top, so the note rests on the high pitch as long as the key is
  // held. Release is short so it cuts cleanly without dropping the
  // pitch back down.
  //
  // Pulse wave at low duty cycle (Width 0.2) gives the nasal
  // harmonic content; a narrow bandpass at 1.8 kHz acts as a
  // throat-formant resonator that lifts the squeak into "asking" range.
  {
    slug: 'fart-squeaky-cheek',
    name: 'SQUEAKY CHEEK',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'pulse', Coarse: 5, Width: 0.2, Level: 0.7 },
    filter: { Type: 'bandpass', Cutoff: 1800, Q: 6 },
    env: { Attack: 0.28, Decay: 0.05, Sustain: 1, Release: 0.06 },
    vca: { Gain: 0.6 },
    mods: [
      // Envelope → Detune (the audio-rate pitch param). modScale 100
      // cents/depth — depth 12 = full octave climb at attack peak.
      // Combined with the 280ms slow attack, pitch glides from
      // Coarse=+5 up to roughly +17 — a clear, perceptible rise.
      { source: 'envelope', target: 'osc', param: 'Detune', depth: 12 },
    ],
  },

  // Wet, splatty. Same chop principle but slower (16 Hz) with
  // heavy distortion + low-bit crush for the gloopy splat texture.
  // Saw + fold (instead of pulse) gives a more uneven harmonic
  // content — reads as "wet" rather than "tight".
  {
    slug: 'fart-wet-blat',
    name: 'WET BLAT',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'saw', Coarse: -19, Level: 0.7, Drive: 0.5, Fold: 4 },
    filter: { Type: 'lowpass', Cutoff: 480, Q: 6 },
    env: { Attack: 0.002, Decay: 0.25, Sustain: 0.4, Release: 0.18 },
    vca: { Gain: 0.65 },
    dist: { Drive: 0.7, Tone: 2200, Mix: 0.7 },
    crush: { Bits: 6, Freq: 7000, Mix: 0.45 },
    lfo: { Wave: 'square', Rate: 16 },
    mods: [
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.9 },
      // Envelope → Detune (audio-rate). 8 semis pop up for the splat
      // attack character.
      { source: 'envelope', target: 'osc', param: 'Detune', depth: 8 },
    ],
  },

  // Very fast chop (32 Hz, getting close to audio-rate) — almost
  // sounds tonal, like a sustained kazoo / duck. Saw a bit higher
  // than the others. The flutter character is the signature here:
  // rapid cheek vibration.
  {
    slug: 'fart-cheek-flapper',
    name: 'CHEEK FLAPPER',
    tags: ['fart', 'mono'],
    voice: { mode: 'mono' },
    osc: { Wave: 'saw', Coarse: -19, Level: 0.7 },
    filter: { Type: 'lowpass', Cutoff: 420, Q: 5 },
    env: { Attack: 0.005, Decay: 0.3, Sustain: 0.7, Release: 0.2 },
    vca: { Gain: 0.65 },
    lfo: { Wave: 'square', Rate: 32 },
    mods: [
      { source: 'lfo', target: 'osc', param: 'Level', depth: 0.95 },
    ],
  },
];

/**
 * The hidden fart preset bank. Returned by `getFartPresets()`; main.ts
 * conditionally splices these into the PresetBrowser's factory list
 * after the user enters the unlock phrase.
 */
export const FART_PRESETS: readonly Preset[] = FART_SPECS.map(makePreset);

/** Convenience lookup — returns null when the id isn't a factory preset. */
export function getFactoryPreset(id: string): Preset | null {
  // Searches both the standard bank and the secret fart bank — once a
  // user has loaded a fart preset it should still resolve by id even
  // mid-session (for undo/redo, recent-preset reload, etc.).
  return (
    FACTORY_PRESETS.find((p) => p.id === id) ??
    FART_PRESETS.find((p) => p.id === id) ??
    null
  );
}

/** True when an id belongs to a factory preset (has the `preset-factory-` prefix). */
export function isFactoryPresetId(id: string): boolean {
  return id.startsWith('preset-factory-');
}
